import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemButton,
  ListItemText,
  Chip,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import DiamondIcon from "@mui/icons-material/Diamond";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import { useNavigate } from "react-router-dom";
import { SESSION_NOTICE_KEY } from "./atsModule/services/session";
import logo from "../assets/logo/daily-tick-logo.png";
import axios from "axios";
import theme from "../theme";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import kumuduLogo from "../assets/logo/kumudu-logo-1.png";

// A store's group membership lives in store_master.common_store, which the login
// response already carries per store: a parent holds its own id, a child holds
// the parent's id, and a standalone store holds NULL. So the picker can group
// itself with no extra call.
//
// The list is every store the number can enter, which is wider than the stores it
// holds a role in: a role anywhere in a group opens the whole group, exactly as
// the in-app store switcher does. Those group-only rows come back with a null
// user_id and no role (`is_member: false`) and are picked by store_id.
const toId = (value) =>
  value === undefined || value === null || value === "" ? null : Number(value);

// store_master flags which module a store runs (is_pawn / is_travel). MySQL
// hands the tinyints back as 0/1, sometimes as strings, so they are coerced
// before being read as booleans.
const isFlagOn = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const isPawnStore = (store) => isFlagOn(store?.is_pawn);
const isTravelStore = (store) => isFlagOn(store?.is_travel);

// The module matters at the picker: the same mobile can hold a role in a pawn
// store and in a travel store, and the two log in to completely different
// screens — so the choice has to be tellable apart before it is made.
const MODULE_BADGES = {
  pawn: { label: "Pawn Shop", fg: "#6D28D9", bg: "#F5F3FF", br: "#DDD6FE" },
  travel: { label: "Travel ERP", fg: "#0F766E", bg: "#F0FDFA", br: "#99F6E4" },
};

// A store can carry both flags, so this is a list rather than one label.
const modulesOf = (store) => {
  const out = [];
  if (isPawnStore(store)) out.push(MODULE_BADGES.pawn);
  if (isTravelStore(store)) out.push(MODULE_BADGES.travel);
  return out;
};

// Module icon wins over the plain storefront so a row reads at a glance; the
// group's parent/branch standing still shows through the rail, the row's tint
// and its own chip.
const StoreRowIcon = ({ store, isParent, inGroup }) => {
  if (isPawnStore(store)) {
    return <DiamondIcon sx={{ color: MODULE_BADGES.pawn.fg }} />;
  }
  if (isTravelStore(store)) {
    return <FlightTakeoffIcon sx={{ color: MODULE_BADGES.travel.fg }} />;
  }
  if (isParent) return <AccountTreeIcon sx={{ color: "#008080" }} />;
  return <StorefrontIcon sx={{ color: inGroup ? "#4c9d9d" : "#008080" }} />;
};

// Sections in order of first appearance, so the list keeps the order the API
// returned. All standalone stores share one section; each group gets its own.
const buildStoreSections = (stores) => {
  const sections = [];
  const sectionIndexByKey = new Map();

  stores.forEach((store) => {
    const groupId = toId(store.common_store);
    const key = groupId === null ? "independent" : `group-${groupId}`;
    const existing = sectionIndexByKey.get(key);

    if (existing !== undefined) {
      sections[existing].stores.push(store);
      return;
    }

    sectionIndexByKey.set(key, sections.length);
    sections.push({
      key,
      groupId,
      isGroup: groupId !== null,
      stores: [store],
    });
  });

  return sections;
};

const LoginPage = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [loginData, setLoginData] = useState({ mobile: "" });
  const [isFocused, setIsFocused] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const [snackBarConfig, setSnackBarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });

  // A mobile number can belong to more than one store, with a different role in
  // each. When it does, the person picks which store they are logging in to.
  const [storeOptions, setStoreOptions] = useState([]);
  const [isStoreDialogOpen, setIsStoreDialogOpen] = useState(false);

  // Independent stores vs connected groups, so the picker can show how the
  // stores relate to each other instead of one flat list.
  const storeSections = useMemo(
    () => buildStoreSections(storeOptions),
    [storeOptions],
  );
  const connectedCount = useMemo(
    () =>
      storeSections
        .filter((section) => section.isGroup)
        .reduce((total, section) => total + section.stores.length, 0),
    [storeSections],
  );

  // --- Utility to clean number and keep only last 10 digits ---
  const extractMobileNumber = (text) => {
    const digits = text.replace(/\D/g, ""); // remove non-digits
    return digits.slice(-10); // take last 10 digits
  };

  // Global paste handler (works even if input not focused)
  useEffect(() => {
    const handleGlobalPaste = (e) => {
      const pastedText = e.clipboardData.getData("text");
      const mobile = extractMobileNumber(pastedText);
      if (mobile.length === 10) {
        setLoginData({ mobile });
        inputRef.current?.focus(); // focus input for clarity
      }
    };

    window.addEventListener("paste", handleGlobalPaste);
    return () => {
      window.removeEventListener("paste", handleGlobalPaste);
    };
  }, []);

  // Auto focus when component mounts
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Why you are looking at this screen.
  //
  // The hiring module renews its session token under the app, so reaching the
  // login page from there means the renewal was refused — idle past the
  // server's refresh window, or the account deactivated. Landing on a bare
  // login form after that reads as the app having lost its place, so the
  // reason is carried across the redirect and shown once.
  useEffect(() => {
    let notice = null;
    try {
      notice = sessionStorage.getItem(SESSION_NOTICE_KEY);
      // Read once and cleared, or it reappears on every later visit to this
      // screen for the rest of the browser session.
      if (notice) sessionStorage.removeItem(SESSION_NOTICE_KEY);
    } catch {
      // Private mode, blocked storage. No notice is only a missing courtesy.
    }

    if (notice) {
      setSnackBarConfig({ open: true, message: notice, severity: "info" });
    }
  }, []);

  const handleClose = (e, reason) => {
    if (reason === "clickaway") return;
    setSnackBarConfig((prevState) => ({ ...prevState, open: false }));
  };

  const handleChange = (e) => {
    const value = e.target.value;
    if (!/^\d{0,10}$/.test(value)) return;
    setLoginData((prevState) => ({ ...prevState, mobile: value }));
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    const mobile = extractMobileNumber(pastedText);
    if (mobile.length === 10) {
      setLoginData({ mobile });
    }
  };

  const inputVerification = () => {
    if (!loginData.mobile.trim()) {
      setSnackBarConfig({
        open: true,
        message: "Enter a mobile number",
        severity: "error",
      });
      return false;
    }

    if (!/^\d{10}$/.test(loginData.mobile)) {
      setSnackBarConfig({
        open: true,
        message: "Enter a valid mobile number",
        severity: "error",
      });
      return false;
    }

    return true;
  };

  // The branch this row would put you in, when that is worth showing.
  //
  // `login_branch_name` is the backend's answer to "where would choosing this
  // land me" — the pinned branch for a Store HOD or Store Staff, the store's
  // main branch for anybody else, and nothing at all for a service role, who
  // is not in a branch.
  //
  // Suppressed when it would only repeat the store's own name. Migration 015
  // named each store's first branch after the store, so 334 of 336 stores would
  // otherwise show "TEST · TEST" — noise that buries the two rows where the
  // branch is genuinely telling you something.
  const branchNoteFor = (store) => {
    const branch = store?.login_branch_name;
    if (!branch) return null;

    const sameAsStore =
      String(branch).trim().toLowerCase() ===
      String(store?.store_name ?? "").trim().toLowerCase();

    if (sameAsStore && Number(store?.branch_count ?? 0) <= 1) return null;
    return branch;
  };

  // Carry the chosen store into OTP verification so the backend returns that
  // store's membership (user row + role) instead of guessing one.
  const proceedToOtp = (store) => {
    navigate("/verify-otp", {
      state: {
        mobile: loginData.mobile,
        store_id: store?.store_id ?? null,
        user_id: store?.user_id ?? null,
        store_name: store?.store_name ?? null,
        role: store?.role ?? null,
        role_label: store?.role_label ?? null,
        branch_id: store?.login_branch_id ?? null,
        branch_name: store?.login_branch_name ?? null,
      },
    });
  };

  const handleStoreSelect = (store) => {
    setIsStoreDialogOpen(false);
    proceedToOtp(store);
  };

  const handleStoreDialogClose = () => {
    setIsStoreDialogOpen(false);
    setStoreOptions([]);
    setIsDisabled(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    // run validation first
    if (!inputVerification()) {
      setIsDisabled(false); // <-- ensure re-enable on validation failure
      return;
    }

    setIsDisabled(true); // <-- disable only if validation passes

    const url = `${process.env.REACT_APP_API_BASE_URL}/api/auth/userlogin`;

    try {
      const res = await axios.post(url, loginData);
      const { success, statusCode, statusMessage, stores } = res.data;

      if (!success) {
        setIsDisabled(false);
        setSnackBarConfig({
          open: true,
          message: statusMessage || "No user found with this mobile number",
          severity: "warning",
        });
      }

      if (success) {
        if (statusCode === 1) {
          const memberships = Array.isArray(stores) ? stores : [];

          setSnackBarConfig({
            open: true,
            message: "OTP sent successfully",
            severity: "success",
          });

          // Belongs to several stores → ask which one before the OTP screen
          if (memberships.length > 1) {
            setStoreOptions(memberships);
            setIsStoreDialogOpen(true);
            return;
          }

          setTimeout(() => {
            proceedToOtp(memberships[0]);
          }, 1000);
        } else {
          setIsDisabled(false);
          setSnackBarConfig({
            open: true,
            message: statusMessage || "Login failed.",
            severity: "error",
          });
        }
      }
    } catch (error) {
      setIsDisabled(false);
      console.error(error);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-around",
        alignItems: "center",
        p: 2,
        bgcolor: "#fff",
      }}
    >
      <Container maxWidth="xs">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "90vh",
            gap: { xs: 6, sm: 8 },
            width: "100%",
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mt: "calc(100px + env(safe-area-inset-top, 24px))",
              position: "fixed",
              top: 0,
            }}
          >
            <Box
              sx={{
                width: 200,
                height: 60,
                backgroundImage: `url(${logo})`,
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
              }}
            />
            <Typography
              sx={{ color: theme.lightTheme.placeholder, fontSize: 12, mt: 1 }}
            >
              Empowering People with smart Solutions
            </Typography>
          </Box>

          {/* Form */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 1,
              width: "100%",
            }}
          >
            <Typography
              align="center"
              sx={{
                fontSize: 24,
                fontWeight: 600,
                mb: 2,
                background:
                  "linear-gradient(90deg, rgba(0,128,128,1) 0%, rgba(0,26,26,1) 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Welcome Back
            </Typography>

            {/* Mobile Input Field */}
            <TextField
              inputRef={inputRef}
              label={isFocused || loginData.mobile ? "" : "Mobile Number"}
              variant="outlined"
              fullWidth
              placeholder="Enter your mobile number"
              value={loginData.mobile}
              onChange={handleChange}
              onPaste={handlePaste}
              onFocus={() => {
                setIsFocused(true);
                setTimeout(() => {
                  inputRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }, 200);
              }}
              onBlur={() => setIsFocused(false)}
              inputProps={{ maxLength: 10, inputMode: "numeric" }}
              sx={{ ...formStyle, height: 54, mb: 2 }}
              disabled={isDisabled}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleLogin(e);
                }
              }}
            />

            {/* Login Button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                width: "100%",
                height: 54,
                background: "linear-gradient(90deg, #008080 0%, #4428A0 100%)",
                color: "white",
                boxShadow: `inset -2px -2px 12px rgba(0,0,0,0.5),
                           0px 4px 20px rgba(0,0,0,0.4)`,
                borderRadius: 2.5,
                py: 1.25,
                cursor: "pointer",
              }}
              onClick={(e) => {
                handleLogin(e);
              }}
            >
              <Typography sx={{ fontSize: 20, color: "white" }}>
                Login
              </Typography>
              <ChevronRightIcon sx={{ fontSize: 20, color: "white" }} />
            </Box>
          </Box>

          {/* Trademark */}
          <Box
            sx={{
              textAlign: "center",
              position: "fixed",
              bottom: "calc(76px + env(safe-area-inset-bottom, 44px))",
            }}
          >
            <img style={{ height: "32px" }} src={kumuduLogo}></img>
            <Typography sx={{ fontSize: 12, mt: 1 }}>
              Kumudu Software solutions LLP @ {new Date().getFullYear()}
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* Store selection — shown only when the mobile belongs to many stores */}
      <Dialog
        open={isStoreDialogOpen}
        onClose={handleStoreDialogClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 600 }}>
            Select a store
          </Typography>
          <Typography
            sx={{ fontSize: 13, color: theme.lightTheme.placeholder }}
          >
            This number can log in to {storeOptions.length} stores
            {connectedCount > 0 && `, ${connectedCount} of them connected`}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ px: 1.5, pb: 2 }}>
          {storeSections.map((section) => {
            // The parent is only nameable when the person is a member of it —
            // otherwise the group is still shown, just without its parent row.
            const parentStore = section.isGroup
              ? section.stores.find(
                  (store) => toId(store.store_id) === section.groupId,
                )
              : null;

            return (
              <Box key={section.key} sx={{ mb: 2, "&:last-of-type": { mb: 0 } }}>
                {/* Section heading — what ties these stores together */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 0.5,
                    mb: 0.75,
                  }}
                >
                  {section.isGroup ? (
                    <AccountTreeIcon sx={{ fontSize: 15, color: "#008080" }} />
                  ) : (
                    <StorefrontIcon sx={{ fontSize: 15, color: "#9aa0a6" }} />
                  )}
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      textTransform: "uppercase",
                      color: section.isGroup ? "#008080" : "#9aa0a6",
                    }}
                  >
                    {section.isGroup
                      ? parentStore
                        ? `Connected stores · ${parentStore.store_name || `Store #${parentStore.store_id}`} group`
                        : "Connected stores"
                      : section.stores.length > 1
                        ? "Independent stores"
                        : "Independent store"}
                  </Typography>
                  {section.isGroup && (
                    <Typography sx={{ fontSize: 11, color: "#9aa0a6" }}>
                      ({section.stores.length})
                    </Typography>
                  )}
                </Box>

                {/* Connected stores sit behind a rail so the group reads as one */}
                <List
                  disablePadding
                  sx={
                    section.isGroup
                      ? {
                          ml: 0.75,
                          pl: 1.25,
                          borderLeft: "2px solid rgba(0,128,128,0.3)",
                        }
                      : undefined
                  }
                >
                  {section.stores.map((store) => {
                    const isParent =
                      section.isGroup &&
                      toId(store.store_id) === section.groupId;

                    // A store reached through the group carries no users row, so
                    // user_id alone is not a key and there is no role to show.
                    const roleLabel = store.role_label || store.role || null;
                    const branchNote = branchNoteFor(store);

                    return (
                      <ListItemButton
                        key={`${store.store_id}-${store.user_id ?? "group"}`}
                        onClick={() => handleStoreSelect(store)}
                        sx={{
                          borderRadius: 2,
                          border: `1px solid ${
                            isParent
                              ? "rgba(0,128,128,0.5)"
                              : theme.lightTheme.textFieldBorder
                          }`,
                          bgcolor: isParent
                            ? "rgba(0,128,128,0.04)"
                            : "transparent",
                          mb: 1,
                          "&:last-of-type": { mb: 0 },
                          gap: 1.5,
                        }}
                      >
                        <StoreRowIcon
                          store={store}
                          isParent={isParent}
                          inGroup={section.isGroup}
                        />
                        <ListItemText
                          primary={
                            store.store_name || `Store #${store.store_id}`
                          }
                          primaryTypographyProps={{
                            fontSize: 15,
                            fontWeight: isParent ? 600 : 500,
                          }}
                          secondary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                mt: 0.5,
                                flexWrap: "wrap",
                              }}
                            >
                              <Chip
                                label={roleLabel || "Group access"}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: 11,
                                  ...(roleLabel
                                    ? {
                                        bgcolor: "rgba(0,128,128,0.1)",
                                        color: "#008080",
                                      }
                                    : {
                                        bgcolor: "#f3f4f6",
                                        color: "#6b7280",
                                      }),
                                }}
                              />
                              {/* Which module this store runs, if any */}
                              {modulesOf(store).map((module) => (
                                <Chip
                                  key={module.label}
                                  label={module.label}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: module.fg,
                                    bgcolor: module.bg,
                                    border: `1px solid ${module.br}`,
                                  }}
                                />
                              ))}
                              {/* Which branch of this store you are about to
                                  enter. Placed after the module chips because
                                  the module decides which app you land in and
                                  the branch only decides where inside it. */}
                              {branchNote && (
                                <Chip
                                  label={branchNote}
                                  size="small"
                                  icon={
                                    <PlaceOutlinedIcon
                                      sx={{ fontSize: 13, ml: 0.5 }}
                                    />
                                  }
                                  sx={{
                                    height: 20,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: "#1E40AF",
                                    bgcolor: "#EFF6FF",
                                    border: "1px solid #BFDBFE",
                                    "& .MuiChip-icon": { color: "#1E40AF" },
                                  }}
                                />
                              )}
                              {/* "Child store", NOT "Branch". This chip is
                                  about the store GROUP — a company that is one
                                  of several under a parent — and calling it a
                                  branch predates branch_master entirely. Now
                                  that a real branch chip sits beside it, the
                                  old wording would have two things on one row
                                  both claiming the same word. */}
                              {section.isGroup && (
                                <Chip
                                  label={isParent ? "Parent store" : "Child store"}
                                  size="small"
                                  variant={isParent ? "filled" : "outlined"}
                                  sx={{
                                    height: 20,
                                    fontSize: 11,
                                    ...(isParent
                                      ? { bgcolor: "#008080", color: "#fff" }
                                      : {
                                          color: "#6b7280",
                                          borderColor: "#d5d8dc",
                                        }),
                                  }}
                                />
                              )}
                            </Box>
                          }
                          secondaryTypographyProps={{ component: "div" }}
                        />
                        <ChevronRightIcon sx={{ color: "#a0a0a0" }} />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>
            );
          })}
        </DialogContent>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackBarConfig.open}
        autoHideDuration={2000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          "&.MuiSnackbar-root": {
            bottom: "56px !important",
          },
        }}
      >
        <Alert
          onClose={handleClose}
          severity={snackBarConfig.severity}
          variant="filled"
        >
          {snackBarConfig.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const formStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    "& fieldset, & .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.lightTheme.textFieldBorder,
      borderRadius: 2,
    },
    "&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.lightTheme.textFieldBorder,
    },
    "&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.lightTheme.textFieldBorder,
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: theme.borders.secondaryBorder,
  },
};

export default LoginPage;
